/**
 * ThemeButton Component
 *
 * Self-contained theme toggle button with Ninja/Sunshine icons
 * Uses ThemeProvider context - no props needed
 */

import React from 'react'
import { Button } from '@innolab-trader/ui'
import { useTheme } from './ThemeProvider'

export interface ThemeButtonProps {
  /** Optional additional CSS classes */
  className?: string
  /** Custom aria-label (defaults to "Toggle theme") */
  ariaLabel?: string
}

/**
 * ThemeButton Component
 *
 * Toggles between Ninja Mode (dark) and Sunshine Mode (light)
 * Fully self-contained - gets theme state from context
 *
 * @example
 * ```tsx
 * import { ThemeButton } from '@innolab-trader/mfe-shared'
 *
 * function MyHeader() {
 *   return <ThemeButton />
 * }
 * ```
 */
export const ThemeButton: React.FC<ThemeButtonProps> = ({
  className = '',
  ariaLabel = 'Toggle theme',
}) => {
  const { isDark, setTheme } = useTheme()

  const handleToggle = () => {
    setTheme(isDark ? 'light' : 'dark')
  }

  const title = `Switch to ${isDark ? 'sunshine' : 'ninja'} mode`

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleToggle}
      className={`p-1.5 text-base ${className}`}
      aria-label={ariaLabel}
      title={title}
    >
      {isDark ? '☀️' : '🥷'}
    </Button>
  )
}
